import React from 'react';
import Helmet from 'react-helmet';
import Translate from '@spordle/intl-elements';

import { useOffice } from '../queries/offices';
import { SKATER, GOALIE } from '../lib';

import Widget from '../components/Widget';
import Pagination from '../components/Pagination'
import { useConfig } from '../components/ConfigContext';
import { Logo } from '../components/Team';
import Loader from '../components/Loader'
import { BackButton } from '../components/BackButton';

import OfficeFilters from '../components/OfficeFilters';

import GameList from '../components/GameList';
import Stats from '../components/Stats';
import Standings from '../components/Standings';
import Sponsors from '../components/Sponsors';

const LeagueHeader = ({ office }) => {
  if (!office) return null;

  return <div className="page-header team-header row d-flex flex-nowrap align-items-center">
    <Logo team={office} />
    <div className="ml-3">
      <h1>{office.name}</h1>
    </div>
  </div>;
}

const LeagueGameList = ({ officeId, seasonId, scheduleId }) =>
  <Pagination perPage={5} hideFirstLastPages>
    <GameList filters={{ officeId, seasonId, scheduleId }} hideScheduleName />
  </Pagination>

const ScheduleSkaterStats = ({ scheduleId, groupId }) =>
  <Pagination>
    <Stats participantType={SKATER} filters={{ scheduleId, groupId }} />
  </Pagination>

const ScheduleGoalieStats = ({ scheduleId, groupId }) =>
  <Pagination>
    <Stats participantType={GOALIE} filters={{ scheduleId, groupId }} />
  </Pagination>

const ScheduleStandings = ({ scheduleId, groupId, ...props }) =>
  <Standings filters={{ scheduleId, groupId }} {...props} />

const StatsSection = ({ filters, ...props }) => {
  const { hideStats } = useConfig();
  const { scheduleId, groupId } = filters;

  if (!scheduleId) {
    return <div className="row">
      <div className="col col-12">
        <h4>
          {!hideStats ? <Translate id="common.lang.stats.and.standings" /> : <Translate id="common.lang.standings" />}
        </h4>
        <Widget gutters>
          <p>{!hideStats ? <Translate id="routes.office.select.schedule.and.stats" /> : <Translate id="routes.office.select.schedule" />}</p>
        </Widget>
      </div>
    </div>
  }

  return <div className="row">
    <div className="col col-12">
      <h4><Translate id="common.lang.standings" /></h4>
      <Widget gutters>
        <ScheduleStandings {...props} scheduleId={scheduleId} groupId={groupId} />
      </Widget>
    </div>

    {!hideStats && <div className="col col-12">
      <h4><Translate id="common.lang.skaters" /></h4>
      <Widget gutters>
        <ScheduleSkaterStats {...props} scheduleId={scheduleId} groupId={groupId} />
      </Widget>
      <h4><Translate id="common.lang.goalies" /></h4>
      <Widget gutters>
        <ScheduleGoalieStats {...props} scheduleId={scheduleId} groupId={groupId} />
      </Widget>
    </div>}
  </div>
}

const Office = ({ officeId, filters, ...props }) => {
  const { seasonId, scheduleId } = filters;
  return <>
    <h4><Translate id="common.lang.calendar" /></h4>
    <Widget>
      <LeagueGameList officeId={officeId} seasonId={seasonId} scheduleId={scheduleId} />
    </Widget>

    <StatsSection filters={filters} {...props} />
  </>
}

export default ({ match, scheduleId, groupId }) => {
  const { officeId } = match.params;
  const { data: office, status, error } = useOffice(officeId);

  if (status === 'loading') {
    return <div className="container team">
      <Loader>
        <Translate id="routes.office.loading" />
      </Loader>
    </div>
  }

  if (status === 'error') {
    let message;
    const status = error?.error?.statusCode;
    if (status === 403) {
      message = <Translate id="routes.office.not.available" />;
    } else if (status === 404) {
      message = <Translate id="routes.office.doesnt.exist" />;
    } else if (status >= 400 && status < 600) {
      message = <Translate id="routes.office.something.went.wrong.with.code" values={{ status }} />;
    } else {
      message = <Translate id="routes.office.something.went.wrong" />;
    }

    return <div className="container team">
      <Helmet meta={[{ name: 'robots', content: 'noindex' }]} />
      <h2>Oops...</h2>
      <p>{message}</p>
      <BackButton />
    </div>
  }

  return <div className="container team">
    {office && <Helmet title={office.name} />}
    <Widget>
      <LeagueHeader office={office} />
    </Widget>

    <Widget gutters>
      <OfficeFilters officeId={officeId} office={office} initialValues={{ scheduleId, groupId }}>
        <Office />
      </OfficeFilters>
    </Widget>

    <Sponsors />
  </div>
}
